import { Check, FileText, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { STEP_NAMES } from './use-clean-text-run';

const WIDTHS = [30, 55, 80, 100] as const;

/**
 * The four named steps, advanced on the client: the run is one request and v1 does not stream, so
 * «pagina n di m» (which needs it) is left out. «Annulla» is always visible.
 */
const stepState = (i: number, step: number): 'done' | 'current' | 'todo' => {
  if (i < step) return 'done';
  return i === step ? 'current' : 'todo';
};

export function ProgressCard({
  name,
  isUrl,
  step,
  onCancel,
}: {
  readonly name: string;
  readonly isUrl: boolean;
  readonly step: number;
  readonly onCancel: () => void;
}) {
  const { t } = useTranslation('tools');
  const Icon = isUrl ? Globe : FileText;
  return (
    <section
      aria-label={t('cleanText.progress.label')}
      className="space-y-4 rounded-xl border border-border bg-card p-4 sm:p-5"
    >
      <div className="flex items-center gap-2 text-sm">
        <Icon aria-hidden="true" className="size-4.5 shrink-0" />
        <b className="min-w-0 truncate">{name}</b>
        <span className="grow" />
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('cleanText.progress.cancel')}
        </Button>
      </div>
      <ol className="space-y-2.5">
        {STEP_NAMES.map((id, i) => {
          const state = stepState(i, step);
          return (
            <li
              key={id}
              data-state={state}
              aria-current={state === 'current' ? 'step' : undefined}
              className={cn(
                'flex items-center gap-2.5 text-sm',
                state === 'todo' && 'text-muted-foreground',
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  'flex size-6 items-center justify-center rounded-full border text-xs',
                  state === 'done' && 'border-transparent bg-[var(--success)] text-white',
                  state === 'current' && 'border-[var(--focus)] font-bold text-[var(--focus)]',
                )}
              >
                {state === 'done' ? <Check className="size-3.5" /> : i + 1}
              </span>
              <span className={cn(state === 'current' && 'font-bold')}>
                {t(`cleanText.progress.steps.${id}`)}
              </span>
            </li>
          );
        })}
      </ol>
      <div
        role="progressbar"
        aria-label={t('cleanText.progress.label')}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={WIDTHS[Math.min(step, 3)]}
        className="h-1.5 overflow-hidden rounded-full bg-muted"
      >
        <span
          className="block h-full rounded-full bg-[var(--progress)] transition-[width]"
          style={{ width: `${WIDTHS[Math.min(step, 3)]}%` }}
        />
      </div>
      <p className="text-sm text-muted-foreground">{t('cleanText.progress.hint')}</p>
    </section>
  );
}
